<template>
  <div>
    <div class="centered-column">
      <div class="container-header">
        <h3> {{ label.get('workweek_overtime') }}</h3>
      </div>
      <div class="white-card">
        <div class="white-card-header">
          <div class="title-wrap">
            <label class="title-label">{{ label.get('workweek_settings') }}</label>
          </div>
        </div>
        <div class="content-wrapper">
          <div class="field-wrap d-flex justify-content-between align-items-center">
            <p class="label">
              {{ label.get('workweek_start_day') }}
            </p>
            <select-dropdown-component
                :value="startDay"
                placeholder="Start Day"
                :options="weekdays"
                @onOptionSelected="onStartDaySelected"
            />
          </div>
        </div>
      </div>
      <div class="white-card">
        <div class="white-card-header">
          <div class="title-wrap">
            <label class="title-label">{{ label.get('overtime_settings') }}</label>
          </div>
          <div class="action-buttons" v-if="saveChangesEnabled">
            <button
                class="update-btn cancel"
                type="button"
                @click="cancel"
            >
              <i class="atto atto-close-btn"/>
              {{ label.get('cancel') }}
            </button>
            <button
                class="update-btn save-changes"
                type="button"
                :disabled="disabledSubmitButton"
                @click="saveChanges"
            >
              <i class="atto atto-selected-tick"/>
              {{ label.get('save_changes') }}
            </button>
          </div>
        </div>
        <div class="overtime-preferences">
          <div class="card-content">
            <div class="d-flex align-items-center justify-content-between">
              <div class="check-wrapper switch-wrapper">
                <b-form-checkbox
                    v-model="weeklyOvertime"
                >
                  {{ label.get('overtime_weekly_overtime') }}
                </b-form-checkbox>
                <p class="disclaimer">
                  {{ label.get('overtime_weekly_overtime_description') }}
                </p>
              </div>
              <div class="overtime-calculations" :class="{'disabled': !weeklyOvertime}">
                <div class="wrapper calculate-after">
                  <p class="label-wrap">
                    <i class="atto atto-rdir-arrow-b"></i>
                    {{ label.get('calculate_after') }}
                  </p>
                  <div class="input-wrapper">
                    <time-picker-component
                        v-model="weeklyCalculateAfter"
                        class="weekly-hours"
                        :format24h="true"
                        :unlimited-hours="true"
                        :add-seconds="false"
                        :max-time="[168, 0, 0]"
                        :required="true"
                    />
                  </div>
                </div>
                <div class="wrapper ot-pay-rate">
                  <p class="label-wrap">
                    <i class="atto atto-rdir-arrow-b"></i>
                    {{ label.get('overtime_pay_rate') }}
                  </p>
                  <div class="input-wrapper">
                    <input type="number"
                           v-model="weeklyPayRate"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr>
          <div class="card-content">
            <div class="d-flex align-items-center justify-content-between">
              <div class="check-wrapper switch-wrapper">
                <b-form-checkbox
                    v-model="dailyOvertime"
                >
                  {{ label.get('overtime_daily_overtime') }}
                </b-form-checkbox>
                <p class="disclaimer">
                  {{ label.get('overtime_daily_overtime_description') }}
                </p>
              </div>
              <div class="overtime-calculations" :class="{'disabled': !dailyOvertime}">
                <div class="wrapper calculate-after">
                  <p class="label-wrap">
                    <i class="atto atto-rdir-arrow-b"/>
                    {{ label.get('calculate_after') }}
                  </p>
                  <div class="input-wrapper">
                    <time-picker-component
                        v-model="dailyCalculateAfter"
                        class="daily-hours"
                        :add-seconds="false"
                        :format24h="true"
                        :required="true"
                    />
                  </div>
                </div>
                <div class="wrapper ot-pay-rate">
                  <p class="label-wrap">
                    <i class="atto atto-rdir-arrow-b"></i>
                    {{ label.get('overtime_pay_rate') }}
                  </p>
                  <div class="input-wrapper">
                    <input type="number"
                           v-model="dailyPayRate"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr>
          <div class="card-content">
            <div class="d-flex align-items-center justify-content-between">
              <div class="check-wrapper switch-wrapper">
                <b-form-checkbox
                    v-model="dailyDoubleOvertime"
                >
                  {{ label.get('overtime_daily_double_overtime') }}
                </b-form-checkbox>
                <p class="disclaimer">
                  {{ label.get('overtime_daily_double_overtime_description') }}
                </p>
              </div>
              <div class="overtime-calculations" :class="{'disabled': !dailyDoubleOvertime}">
                <div class="wrapper calculate-after">
                  <p class="label-wrap">
                    <i class="atto atto-rdir-arrow-b"/>
                    {{ label.get('calculate_after') }}
                  </p>
                  <div class="input-wrapper">
                    <time-picker-component
                        v-model="dailyDoubleCalculateAfter"
                        class="daily-double-hours"
                        :add-seconds="false"
                        :format24h="true"
                        :required="true"
                    />
                  </div>
                </div>
                <div class="wrapper ot-pay-rate">
                  <p class="label-wrap">
                    <i class="atto atto-rdir-arrow-b"></i>
                    {{ label.get('overtime_pay_rate') }}
                  </p>
                  <div class="input-wrapper">
                    <input type="number"
                           v-model="dailyDoublePayRate"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr>
          <div class="card-content seventh-day" v-if="customViewAccess">
            <div class="d-flex align-items-center justify-content-between">
              <div class="check-wrapper switch-wrapper">
                <b-form-checkbox
                    v-model="seventhDayOvertime"
                >
                  {{ label.get('7th_day_overtime') }}
                </b-form-checkbox>
                <p class="disclaimer">
                  {{ label.get('7th_day_overtime_description') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="white-card" v-if="customViewAccess">
        <div class="white-card-header">
          <div class="title-wrap">
            <label class="title-label">{{ label.get('advanced_pay_settings') }}</label>
          </div>
          <div class="action-buttons" v-if="saveAdvancedPayChangesEnabled">
            <button
                class="update-btn cancel"
                type="button"
                @click="cancelAdvancedPay"
            >
              <i class="atto atto-close-btn"/>
              {{ label.get('cancel') }}
            </button>
            <button
                class="update-btn save-changes"
                type="button"
                :disabled="disabledAdvancedSubmitButton"
                @click="saveAdvancedPayChanges"
            >
              <i class="atto atto-selected-tick"/>
              {{ label.get('save_changes') }}
            </button>
          </div>
        </div>
        <div class="overtime-preferences">
          <div class="card-content">
            <div class="d-flex align-items-center justify-content-between">
              <div class="check-wrapper switch-wrapper">
                <b-form-checkbox
                    v-model="saturdaysOvertime"
                >
                  {{ label.get('saturdays') }}
                </b-form-checkbox>
                <p class="disclaimer">
                  {{ label.get('saturdays_overtime_description') }}
                </p>
              </div>
              <div class="overtime-calculations" :class="{'disabled': !saturdaysOvertime}">
                <div class="wrapper ot-pay-rate">
                  <p class="label-wrap">
                    <i class="atto atto-rdir-arrow-b"></i>
                    {{ label.get('pay_rate') }}
                  </p>
                  <div class="input-wrapper">
                    <input type="number"
                           v-model="saturdaysPayRate"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr>
          <div class="card-content">
            <div class="d-flex align-items-center justify-content-between">
              <div class="check-wrapper switch-wrapper">
                <b-form-checkbox
                    v-model="sundaysOvertime"
                >
                  {{ label.get('sundays') }}
                </b-form-checkbox>
                <p class="disclaimer">
                  {{ label.get('sundays_overtime_description') }}
                </p>
              </div>
              <div class="overtime-calculations" :class="{'disabled': !sundaysOvertime}">
                <div class="wrapper ot-pay-rate">
                  <p class="label-wrap">
                    <i class="atto atto-rdir-arrow-b"></i>
                    {{ label.get('pay_rate') }}
                  </p>
                  <div class="input-wrapper">
                    <input type="number"
                           v-model="sundaysPayRate"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr>
          <div class="card-content">
            <div class="d-flex align-items-center justify-content-between">
              <div class="check-wrapper switch-wrapper">
                <b-form-checkbox
                    v-model="publicHolidays"
                >
                  {{ label.get('public_holidays') }}
                </b-form-checkbox>
                <p class="disclaimer public-holidays">
                  {{ label.get('public_holidays_description') }}
                  <button v-if="publicHolidays" @click="managePublicHolidays">
                    <i class="atto atto-rdir-arrow-b" />
                    <span>{{ label.get('manage_public_holidays') }}</span>
                  </button>
                </p>
              </div>
              <div class="overtime-calculations" :class="{'disabled': !publicHolidays}">
                <div class="wrapper ot-pay-rate">
                  <p class="label-wrap">
                    <i class="atto atto-rdir-arrow-b"></i>
                    {{ label.get('pay_rate') }}
                  </p>
                  <div class="input-wrapper">
                    <input type="number"
                           v-model="publicHolidaysPayRate"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr>
          <div class="card-content">
            <div class="d-flex align-items-center justify-content-between">
              <div class="check-wrapper switch-wrapper">
                <b-form-checkbox
                    v-model="nightShiftsOvertime"
                >
                  {{ label.get('night_shifts') }}
                </b-form-checkbox>
                <p class="disclaimer">
                  {{ label.get('night_shifts_description') }}
                </p>
              </div>
              <div class="overtime-calculations calculate-between" :class="{'disabled': !nightShiftsOvertime}">
                <div class="wrapper calculate-after">
                  <p class="label-wrap">
                    <i class="atto atto-rdir-arrow-b"></i>
                    {{ label.get('calculate_between') }}
                  </p>
                  <div class="input-wrap">
                    <time-picker-component
                        v-model="nightShiftCalculateFrom"
                        class="weekly-hours"
                        :required="true"
                    />
                    <i class="atto atto-rdir-arrow-b"></i>
                    <time-picker-component
                        v-model="nightShiftCalculateTill"
                        :required="true"
                    />
                  </div>
                </div>
                <div class="wrapper ot-pay-rate">
                  <p class="label-wrap">
                    <i class="atto atto-rdir-arrow-b"></i>
                    {{ label.get('pay_rate') }}
                  </p>
                  <div class="input-wrapper">
                    <input type="number"
                           v-model="nightShiftsPayRate"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <public-holidays-component
        v-if="managePublicHolidaysModal"
    />
  </div>
</template>

<script>
import SelectDropdownComponent from '../../../shared/components/SelectDropdownComponent';
import TimePickerComponent from '../../../features/timesheets/shared/components/TimePickerComponent';
import CompanyService from "../company/services/CompanyService";
import UtilsService from "../../../shared/services/UtilsService";
import PublicHolidaysComponent from "./components/PublicHolidaysComponent";
import WorkweekOvertimeService from "./services/WorkweekOvertimeService";

export default {
  name: "WorkweekOvertimeComponent",
  components: {
    PublicHolidaysComponent,
    SelectDropdownComponent,
    TimePickerComponent
  },
  data() {
    return {
      startDay: this.$store.state.companySettings.start_day,
      weekdays: UtilsService.getMappedWeekdays(this.$store.state.weekdays),
      weeklyOvertime: this.$store.state.companySettings.weekly_overtime,
      weeklyCalculateAfter: this.$store.state.companySettings.weekly_calculate_after,
      weeklyPayRate: this.$store.state.companySettings.weekly_pay_rate,
      dailyOvertime: this.$store.state.companySettings.daily_overtime,
      dailyCalculateAfter: this.$store.state.companySettings.daily_calculate_after,
      dailyPayRate: this.$store.state.companySettings.daily_pay_rate,
      dailyDoubleOvertime: this.$store.state.companySettings.daily_double_overtime,
      dailyDoubleCalculateAfter: this.$store.state.companySettings.daily_double_calculate_after,
      dailyDoublePayRate: this.$store.state.companySettings.daily_double_pay_rate,
      disabled: false,
      disabledSubmitButton: false,
      seventhDayOvertime: this.$store.state.companySettings.seventh_day_overtime,
      saturdaysOvertime: this.$store.state.companySettings.saturdays,
      saturdaysPayRate: this.$store.state.companySettings.saturdays_pay_rate,
      sundaysOvertime: this.$store.state.companySettings.sundays,
      sundaysPayRate: this.$store.state.companySettings.sundays_pay_rate,
      publicHolidays: this.$store.state.companySettings.public_holidays,
      publicHolidaysPayRate: this.$store.state.companySettings.public_holidays_pay_rate,
      nightShiftsOvertime: this.$store.state.companySettings.night_shifts,
      nightShiftCalculateFrom: this.$store.state.companySettings.night_shift_calculate_from,
      nightShiftCalculateTill: this.$store.state.companySettings.night_shift_calculate_till,
      nightShiftsPayRate: this.$store.state.companySettings.night_shift_pay_rate,
      disabledAdvancedSubmitButton: false,
      managePublicHolidaysModal: false,
    }
  },
  computed: {
    saveChangesEnabled() {
      return this.weeklyOvertime !== this.$store.state.companySettings.weekly_overtime ||
          this.weeklyCalculateAfter !== this.$store.state.companySettings.weekly_calculate_after ||
          this.weeklyPayRate !== this.$store.state.companySettings.weekly_pay_rate ||
          this.dailyOvertime !== this.$store.state.companySettings.daily_overtime ||
          this.dailyCalculateAfter !== this.$store.state.companySettings.daily_calculate_after ||
          this.dailyPayRate !== this.$store.state.companySettings.daily_pay_rate ||
          this.dailyDoubleOvertime !== this.$store.state.companySettings.daily_double_overtime ||
          this.dailyDoubleCalculateAfter !== this.$store.state.companySettings.daily_double_calculate_after ||
          this.dailyDoublePayRate !== this.$store.state.companySettings.daily_double_pay_rate;
    },
    saveAdvancedPayChangesEnabled() {
      return this.seventhDayOvertime !== this.$store.state.companySettings.seventh_day_overtime ||
          this.saturdaysOvertime !== this.$store.state.companySettings.saturdays ||
          this.saturdaysPayRate !== this.$store.state.companySettings.saturdays_pay_rate ||
          this.sundaysOvertime !== this.$store.state.companySettings.sundays ||
          this.sundaysPayRate !== this.$store.state.companySettings.sundays_pay_rate ||
          this.publicHolidays !== this.$store.state.companySettings.public_holidays ||
          this.publicHolidaysPayRate !== this.$store.state.companySettings.public_holidays_pay_rate ||
          this.nightShiftsOvertime !== this.$store.state.companySettings.night_shifts ||
          this.nightShiftCalculateFrom !== this.$store.state.companySettings.night_shift_calculate_from ||
          this.nightShiftCalculateTill !== this.$store.state.companySettings.night_shift_calculate_till ||
          this.nightShiftsPayRate !== this.$store.state.companySettings.night_shift_pay_rate;
    },
    customViewAccess() {
      return false;
    },
  },
  mounted() {
    this.$root.$on("onPublicHolidaysClose", this.onPublicHolidaysClose)
  },
  beforeDestroy() {
    this.$root.$off("onPublicHolidaysClose")
  },
  methods: {
    onStartDaySelected(item) {
      this.startDay = item.value;
      this.$store.state.companySettings.start_day = this.startDay;
      CompanyService.updateCompanySettings(this.$store.state.companySettings).then((response) => {
        this.$store.commit('hideLoading');
        this.$awn.success(response.data.message);
      }).catch(() => {
        this.$store.commit('hideLoading');
      });
    },
    managePublicHolidays: function () {
      this.managePublicHolidaysModal = true;
    },
    saveChanges() {
      this.$store.commit('showLoading');
      this.disabledSubmitButton = true;

      this.resetEmptyOvertimeValuesToDefault();

      this.$store.state.companySettings.weekly_overtime = this.weeklyOvertime;
      this.$store.state.companySettings.weekly_calculate_after = this.weeklyCalculateAfter;
      this.$store.state.companySettings.weekly_pay_rate = this.weeklyPayRate;
      this.$store.state.companySettings.daily_overtime = this.dailyOvertime;
      this.$store.state.companySettings.daily_calculate_after = this.dailyCalculateAfter;
      this.$store.state.companySettings.daily_pay_rate = this.dailyPayRate;
      this.$store.state.companySettings.daily_double_overtime = this.dailyDoubleOvertime;
      this.$store.state.companySettings.daily_double_calculate_after = this.dailyDoubleCalculateAfter;
      this.$store.state.companySettings.daily_double_pay_rate = this.dailyDoublePayRate;
      this.$store.state.companySettings.seventh_day_overtime = this.seventhDayOvertime;

      CompanyService.updateCompanySettings(this.$store.state.companySettings).then((response) => {
        this.$store.commit('hideLoading');
        this.disabledSubmitButton = false;
        this.$awn.success(response.data.message);
      }).catch(() => {
        this.$store.commit('hideLoading');
        this.disabledSubmitButton = false;
      });
    },
    cancel() {
      this.weeklyOvertime = this.$store.state.companySettings.weekly_overtime;
      this.weeklyCalculateAfter = this.$store.state.companySettings.weekly_calculate_after;
      this.weeklyPayRate = this.$store.state.companySettings.weekly_pay_rate;

      this.dailyOvertime = this.$store.state.companySettings.daily_overtime;
      this.dailyCalculateAfter = this.$store.state.companySettings.daily_calculate_after;
      this.dailyPayRate = this.$store.state.companySettings.daily_pay_rate;

      this.dailyDoubleOvertime = this.$store.state.companySettings.daily_double_overtime;
      this.dailyDoubleCalculateAfter = this.$store.state.companySettings.daily_double_calculate_after;
      this.dailyDoublePayRate = this.$store.state.companySettings.daily_double_pay_rate;

      this.seventhDayOvertime = this.$store.state.companySettings.seventh_day_overtime;

      this.disabledSubmitButton = false;
    },
    onPublicHolidaysClose(){
      this.managePublicHolidaysModal = false;
    },
    saveAdvancedPayChanges() {
      this.$store.commit('showLoading');
      this.disabledAdvancedSubmitButton = true;

      this.resetEmptyAdvancedPayValuesToDefault();

      this.$store.state.companySettings.saturdays = this.saturdaysOvertime;
      this.$store.state.companySettings.saturdays_pay_rate = this.saturdaysPayRate;
      this.$store.state.companySettings.sundays = this.sundaysOvertime;
      this.$store.state.companySettings.sundays_pay_rate = this.sundaysPayRate;
      this.$store.state.companySettings.public_holidays = this.publicHolidays;
      this.$store.state.companySettings.public_holidays_pay_rate = this.publicHolidaysPayRate;
      this.$store.state.companySettings.night_shifts = this.nightShiftsOvertime;
      this.$store.state.companySettings.night_shift_calculate_from = this.nightShiftCalculateFrom;
      this.$store.state.companySettings.night_shift_calculate_till = this.nightShiftCalculateTill;
      this.$store.state.companySettings.night_shift_pay_rate = this.nightShiftsPayRate;

      CompanyService.updateCompanySettings(this.$store.state.companySettings).then((response) => {
        this.$store.commit('hideLoading');
        this.disabledAdvancedSubmitButton = false;
        this.$awn.success(response.data.message);
      }).catch(() => {
        this.$store.commit('hideLoading');
        this.disabledAdvancedSubmitButton = false;
      });
    },
    cancelAdvancedPay() {
      this.saturdaysOvertime = this.$store.state.companySettings.saturdays;
      this.saturdaysPayRate = this.$store.state.companySettings.saturdays_pay_rate;

      this.sundaysOvertime = this.$store.state.companySettings.sundays;
      this.sundaysPayRate = this.$store.state.companySettings.sundays_pay_rate;
      this.publicHolidays = this.$store.state.companySettings.public_holidays;

      this.publicHolidaysPayRate = this.$store.state.companySettings.public_holidays_pay_rate;
      this.nightShiftsOvertime = this.$store.state.companySettings.night_shifts;
      this.nightShiftCalculateFrom = this.$store.state.companySettings.night_shift_calculate_from;
      this.nightShiftCalculateTill = this.$store.state.companySettings.night_shift_calculate_till;
      this.nightShiftsPayRate = this.$store.state.companySettings.night_shift_pay_rate;

      this.disabledAdvancedSubmitButton = false;
    },
    resetEmptyOvertimeValuesToDefault() {
      if (this.weeklyCalculateAfter === "" || this.weeklyCalculateAfter === null || this.weeklyCalculateAfter === undefined) {
        this.weeklyCalculateAfter = this.$store.state.companySettings.weekly_calculate_after;
      }
      if (this.weeklyPayRate === "" || this.weeklyPayRate === null || this.weeklyPayRate === undefined) {
        this.weeklyPayRate = this.$store.state.companySettings.weekly_pay_rate;
      }
      if (this.dailyCalculateAfter === "" || this.dailyCalculateAfter === null || this.dailyCalculateAfter === undefined) {
        this.dailyCalculateAfter = this.$store.state.companySettings.daily_calculate_after;
      }
      if (this.dailyPayRate === "" || this.dailyPayRate === null || this.dailyPayRate === undefined) {
        this.dailyPayRate = this.$store.state.companySettings.daily_pay_rate;
      }
      if (this.dailyDoubleCalculateAfter === "" || this.dailyDoubleCalculateAfter === null || this.dailyDoubleCalculateAfter === undefined) {
        this.dailyDoubleCalculateAfter = this.$store.state.companySettings.daily_double_calculate_after;
      }
      if (this.dailyDoublePayRate === "" || this.dailyDoublePayRate === null || this.dailyDoublePayRate === undefined) {
        this.dailyDoublePayRate = this.$store.state.companySettings.daily_double_pay_rate;
      }
    },
    resetEmptyAdvancedPayValuesToDefault() {
      if (this.seventhDayOvertime === "" || this.seventhDayOvertime === null || this.seventhDayOvertime === undefined) {
        this.seventhDayOvertime = this.$store.state.companySettings.seventh_day_overtime;
      }
      if (this.saturdaysOvertime === "" || this.saturdaysOvertime === null || this.saturdaysOvertime === undefined) {
        this.saturdaysOvertime = this.$store.state.companySettings.saturdays;
      }
      if (this.saturdaysPayRate === "" || this.saturdaysPayRate === null || this.saturdaysPayRate === undefined) {
        this.saturdaysPayRate = this.$store.state.companySettings.saturdays_pay_rate;
      }
      if (this.sundaysOvertime === "" || this.sundaysOvertime === null || this.sundaysOvertime === undefined) {
        this.sundaysOvertime = this.$store.state.companySettings.sundays;
      }
      if (this.sundaysPayRate === "" || this.sundaysPayRate === null || this.sundaysPayRate === undefined) {
        this.sundaysPayRate = this.$store.state.companySettings.sundays_pay_rate;
      }
      if (this.publicHolidays === "" || this.publicHolidays === null || this.publicHolidays === undefined) {
        this.publicHolidays = this.$store.state.companySettings.public_holidays;
      }
      if (this.publicHolidaysPayRate === "" || this.publicHolidaysPayRate === null || this.publicHolidaysPayRate === undefined) {
        this.publicHolidaysPayRate = this.$store.state.companySettings.public_holidays_pay_rate;
      }
      if (this.nightShiftsOvertime === "" || this.nightShiftsOvertime === null || this.nightShiftsOvertime === undefined) {
        this.nightShiftsOvertime = this.$store.state.companySettings.night_shifts;
      }
      if ( this.nightShiftCalculateFrom === "" ||
          this.nightShiftCalculateFrom === null ||
          this.nightShiftCalculateFrom === "00:00:00" ||
          this.nightShiftCalculateFrom === undefined
      ) {
        this.nightShiftCalculateFrom = this.$store.state.companySettings.night_shift_calculate_from;
      }
      if ( this.nightShiftCalculateTill === "" ||
          this.nightShiftCalculateTill === null ||
          this.nightShiftCalculateTill === "00:00:00" ||
          this.nightShiftCalculateTill === undefined
      ) {
        this.nightShiftCalculateTill = this.$store.state.companySettings.night_shift_calculate_till;
      }
      if (this.nightShiftsPayRate === "" || this.nightShiftsPayRate === null || this.nightShiftsPayRate === undefined) {
        this.nightShiftsPayRate = this.$store.state.companySettings.night_shift_pay_rate;
      }
    }
  }
}
</script>

<style lang="scss" scoped>

.overtime-preferences{
  hr{
    margin: 0 40px 0 24px;
  }
  .seventh-day{
    padding-top: 48px;
    padding-bottom: 60px;
  }
  p.disclaimer{
    margin-top: 4px;
    &.public-holidays{
      flex-flow: column;
      justify-content: flex-start;
      align-items: flex-start;

      button{
        display: flex;
        align-items: center;
        justify-content: flex-start;
        color: $atto-green;
        padding: 0;
        margin-top: 16px;
        i{
          width: 24px;
          height: 24px;
          text-decoration: none;
          font-size: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 4px;
        }
        span{
          @include fontSize(14px);
          @include lHeight(28px);
          font-family: $roboto-font;
          text-decoration: underline;
          text-transform: capitalize;
        }
      }
    }
  }
  .card-content{
    .calculate-between{
      align-items: flex-end;
      .wrapper{
        width: auto;

        &.ot-pay-rate{
          max-width: 260px;

          .input-wrapper{
            margin-left: 83px;
          }
        }
        &.calculate-after{
          .input-wrap{
            margin-left: 19px;
            display: flex;
            flex-flow: row;
            justify-content: space-between;
            align-items: center;
            i{
              width: 24px;
              height: 24px;
              @include fontSize(8px);
              display: inline-flex;
              justify-content: center;
              align-items: center;
              color: $atto-dark-grey;
            }
            input{
              width: 96px;
            }
          }
          .label-wrap{
            min-width: 137px;
          }
        }
      }
    }
  }
}
.field-wrap {
  .label {
    font: $roboto-regular 0.9375rem/1.75rem $roboto-font;
    color: $atto-font-color;
    margin: 0;
  }

  .atto-select {
    width: 184px;
  }
}

.card-content {
  .switch-wrapper {
    margin-left: 16px;
  }
}
</style>
<style lang="scss">
.subsection {
  padding: 42px 0 16px;
  border-bottom: 2px solid $atto-light-grey;

  &.no-border {
    border-bottom: 0;
    padding-bottom: 0;
  }
}

.overtime-radios {
  .check-wrapper {
    margin-bottom: 32px;

    p {
      color: $atto-dark-grey;
      font-family: $roboto-font;
      font-size: 0.875rem;
      font-weight: 400;
      line-height: 1.25rem;
      display: inline-flex;
      padding-left: 40px;
      margin-bottom: 0;
      margin-top: 12px;
    }
  }

  > :last-child {
    margin-bottom: 0;
  }
}
</style>